import type { LeaseIntakeStatus } from "@/app/api/leaseIntakes"
import type { ClientTheme } from "@/app/lib/themes"

// Same amber the Lease Schedule uses for "needs attention" (prototype
// `--amber: #8A6A1F`).
export const AMBER = "#8A6A1F"

// Mirrors the server's allow-list (PDF, images, CSV, .docx). Legacy binary
// .doc is rejected client-side with the same guidance the server gives.
export const ACCEPT =
  ".pdf,application/pdf,.png,image/png,.jpg,.jpeg,image/jpeg,.csv,text/csv,.docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"

const ALLOWED_EXT = ["pdf", "png", "jpg", "jpeg", "csv", "docx"]

// Mirrors the server's per-kind caps (LeaseIntakesController::MAX_BYTES_BY_KIND).
const MB = 1024 * 1024
const MAX_MB_BY_EXT: Record<string, number> = {
  pdf: 22,
  png: 5,
  jpg: 5,
  jpeg: 5,
  csv: 32,
  docx: 32,
}

/** Returns an error message for a file we know the server will refuse, else null. */
export function validateIntakeFile(file: File): string | null {
  const ext = file.name.split(".").pop()?.toLowerCase() ?? ""
  if (ext === "doc") {
    return "Legacy Word .doc files aren't supported — save it as .docx or PDF and try again."
  }
  if (!ALLOWED_EXT.includes(ext)) {
    return "Unsupported file type. Upload a PDF, PNG/JPG image, CSV, or .docx."
  }
  const maxMb = MAX_MB_BY_EXT[ext]
  if (file.size > maxMb * MB) {
    return `That file is too large — ${ext.toUpperCase()} files can be at most ${maxMb} MB. Split it into smaller files and upload each one.`
  }
  return null
}

export const STATUS_LABELS: Record<LeaseIntakeStatus, string> = {
  queued: "Queued",
  extracting: "Extracting",
  review: "Ready for review",
  committing: "Committing",
  committed: "Committed",
  discarded: "Discarded",
  failed: "Failed",
}

export function statusColor(status: LeaseIntakeStatus, theme: ClientTheme): string {
  switch (status) {
    case "queued":
    case "extracting":
    case "committing":
      return theme.brandPrimary
    case "review":
      return AMBER
    case "committed":
      return theme.brandTeal
    case "failed":
      return theme.brandAccent
    case "discarded":
      return theme.textDim
  }
}

export function StatusBadge({ status, theme }: { status: LeaseIntakeStatus; theme: ClientTheme }) {
  const color = statusColor(status, theme)
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        fontFamily: "var(--font-ui)",
        fontSize: "11px",
        fontWeight: 600,
        padding: "2px 8px",
        borderRadius: "999px",
        whiteSpace: "nowrap",
        color,
        backgroundColor: `${color}14`,
        border: `1px solid ${color}40`,
      }}
    >
      {STATUS_LABELS[status]}
    </span>
  )
}

export function Card({
  theme,
  children,
  className = "",
}: {
  theme: ClientTheme
  children: React.ReactNode
  className?: string
}) {
  return (
    <div
      className={`rounded-lg p-5 ${className}`}
      style={{ backgroundColor: theme.bgSurface, border: `1px solid ${theme.borderColor}` }}
    >
      {children}
    </div>
  )
}

export function PanelLabel({ children, theme }: { children: React.ReactNode; theme: ClientTheme }) {
  return (
    <h3
      style={{
        fontFamily: "var(--font-ui)",
        fontSize: "10px",
        fontWeight: 600,
        letterSpacing: "0.05em",
        textTransform: "uppercase",
        color: theme.textMuted,
        marginBottom: "12px",
      }}
    >
      {children}
    </h3>
  )
}

export function mutedText(theme: ClientTheme, size = "12px"): React.CSSProperties {
  return { fontFamily: "var(--font-ui)", fontSize: size, color: theme.textMuted }
}

export function headStyle(theme: ClientTheme): React.CSSProperties {
  return {
    fontFamily: "var(--font-ui)",
    fontSize: "10px",
    fontWeight: 600,
    color: theme.textDim,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    whiteSpace: "nowrap",
  }
}

export function formatWhen(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  })
}
